import { ApiError } from './api';

/**
 * Map raw server error codes to user-friendly Chinese messages.
 *
 * Server uses snake_case codes like `proxy_already_bound` for log clarity
 * and programmatic client handling; UI users want plain Chinese.
 *
 * Strategy: longest-prefix match against the message string. Codes can
 * carry a `:detail` suffix (e.g. `session_probe_failed:AUTH_KEY_UNREGISTERED`),
 * which surfaces as a friendly base + raw detail in parens.
 */
const TABLE: Array<[string | RegExp, string]> = [
  // ---- auth ----
  ['invalid_credentials', '邮箱或密码错误'],
  ['missing_token', '请先登录'],
  ['expired_token', '登录已过期，请重新登录'],
  ['insufficient_role', '权限不足'],
  ['account_disabled', '账号已禁用'],

  // ---- proxy ----
  ['proxy_endpoint_exists', '该代理 endpoint 已存在'],
  [/^proxy_in_use_by_(\d+)_account/, '代理仍被 $1 个账号使用，先迁移账号再删'],
  ['proxy_not_found', '代理不存在'],

  // ---- account ----
  ['proxy_already_bound', '此代理已被其它账号占用（旧的 1:1 约束，应该不再出现）'],
  ['tg_credentials_not_configured', 'TG API 凭据未配置 — .env 里缺 TELEGRAM_API_ID/HASH'],
  [/^session_probe_failed:AUTH_KEY_UNREGISTERED/, 'Session 已被 TG 注销，请重新登录账号'],
  [/^session_probe_failed:/, 'Session 校验失败（详情见末尾）'],
  ['duplicate_session', '这个 session 字符串已经导入过了'],
  [/^duplicate_tg_user:/, '这个 TG 账号已经导入过了（同一个 TG 用户）'],
  [/^refresh_profile_failed:/, '账号不在线时无法刷新（需要 active+ready）'],
  ['account_not_found', '账号不存在'],
  ['account_must_be_cooling_or_banned_before_delete', '只有 cooling / banned 状态的账号才能删除'],

  // ---- groups ----
  ['group_not_found', '群不存在'],
  [/^account_not_active:/, '账号不是 active 状态，无法加群'],
  [/^account_not_observer:/, '只有 observer 用途的账号能加群（sender 账号不允许）'],
  [/^group_already_(left|kicked)/, '群已经是 $1 状态了'],
  ['no_account_to_leave_with — group has no joinedByAccountId; pass byAccountId',
    '这个群没有记录"是哪个账号加的"，需要指定 byAccountId'],

  // ---- leads (sales) ----
  ['lead_not_found', '线索不存在'],
  ['lead_not_visible', '看不到这条线索（不在你认领的范围内）'],
  ['already_claimed_by_other_rep', '已被其它销售认领'],
  [/^cannot_claim_from_status:/, '当前状态下不能认领'],
  [/^cannot_release_from_status:/, '当前状态下不能释放'],
  ['not_assignee', '只有当前负责人能操作'],
  ['stale_version', '版本过期 — 别人刚更新过这条线索，刷新后再试'],

  // ---- filter ----
  ['pattern_already_exists', '这条规则已存在'],
  ['pattern_empty', '规则不能为空'],

  // ---- sales user ----
  ['email_already_exists', '该邮箱已被占用'],
  ['cannot_delete_self', '不能删除自己'],
  ['sales_user_not_found', '用户不存在'],

  // ---- generic ----
  ['internal_error', '服务器错误，请稍后重试'],
];

export function friendlyError(err: unknown): string {
  const raw = err instanceof ApiError ? err.message : String(err);
  for (const [match, friendly] of TABLE) {
    if (typeof match === 'string') {
      if (raw === match) return friendly;
    } else {
      const m = raw.match(match);
      if (m) {
        // Simple $1 substitution
        return friendly.replace(/\$(\d+)/g, (_, i) => m[Number(i)] ?? '');
      }
    }
  }
  // Strip noisy zod-style prefix when nothing matched.
  if (raw.startsWith('validation_failed:')) {
    return `字段校验失败：${raw.slice('validation_failed:'.length)}`;
  }
  return raw;
}
